"use client";

import { useCallback } from "react";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  useLazyGetNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
} from "../api/notifications-api";
import {
  appendNotifications,
  selectNotifications,
  selectNotificationsFetched,
  selectNotificationsHasMore,
  selectNotificationsSocketConnected,
  selectUnreadNotificationCount,
} from "../state/notifications-slice";

const NOTIFICATIONS_PAGE_SIZE = 20;

export function useNotifications() {
  const dispatch = useAppDispatch();
  const notifications = useAppSelector(selectNotifications);
  const unreadCount = useAppSelector(selectUnreadNotificationCount);
  const hasFetched = useAppSelector(selectNotificationsFetched);
  const hasMore = useAppSelector(selectNotificationsHasMore);
  const isSocketConnected = useAppSelector(selectNotificationsSocketConnected);
  const [loadMoreNotifications, loadMoreNotificationsState] =
    useLazyGetNotificationsQuery();
  const [markNotificationRead, markNotificationReadState] =
    useMarkNotificationReadMutation();
  const [markAllNotificationsRead, markAllNotificationsReadState] =
    useMarkAllNotificationsReadMutation();

  const loadMore = useCallback(async () => {
    if (!hasFetched || !hasMore || loadMoreNotificationsState.isFetching) {
      return;
    }

    const nextPage = await loadMoreNotifications({
      limit: NOTIFICATIONS_PAGE_SIZE,
      skip: notifications.length,
    }).unwrap();

    dispatch(appendNotifications(nextPage));
  }, [
    dispatch,
    hasFetched,
    hasMore,
    loadMoreNotifications,
    loadMoreNotificationsState.isFetching,
    notifications.length,
  ]);

  return {
    notifications,
    unreadCount,
    hasFetched,
    hasMore,
    loadMore,
    isFetchingMore: loadMoreNotificationsState.isFetching,
    isSocketConnected,
    markNotificationRead,
    markAllNotificationsRead,
    isMarkingSingle: markNotificationReadState.isLoading,
    isMarkingAll: markAllNotificationsReadState.isLoading,
  };
}
